import React, { useRef, useState } from "react";
import DictatedInput from "./DictatedInput.jsx";

/**
 * ReflectionPrompt — "One thing you'll remember about Maris?"
 *
 * Shown on the player's end card, above the forward actions — an
 * invitation, not a gate (Oct 17): one line, text or mic, Save; leaving
 * without answering is just not answering. Not a test: no right answer,
 * never scored. What she writes is echoed back to her on the Meet
 * page next time ("Last time you said…") and is visible to the coordinator.
 *
 * afterRef says what she just watched — "full" or a scene_id — so the echo
 * can say "after Meet Maris". clientId null (preview, share link) hides the
 * prompt entirely: a reflection is attributed or it is noise.
 */
export default function ReflectionPrompt({ clientId, clientName, objectPronoun = null, afterRef = "full", onDone }) {
  const [text, setText] = useState("");
  const [dictated, setDictated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const inputRef = useRef(null);

  if (!clientId) return null;
  async function save() {
    inputRef.current?.stop();
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(`/api/clients/${clientId}/reflections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: t, after_ref: afterRef, dictated }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || "Couldn't save that.");
      }
      setSaved(true);
      onDone?.({ saved: true, text: t });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (saved) {
    return <p style={s.thanks}>Noted. You'll see it next time you open {clientName ? `${clientName}'s profile` : "the profile"}.</p>;
  }

  return (
    <div style={s.wrap}>
      {/* "about her?" when pronouns are known, "about Maris?" when only the
          name is, and the plain question when neither — never a guess. */}
      <p style={s.q}>
        {/* Softer, a closing line rather than a test (Oct 17). */}
        {objectPronoun ? `Before you go — one thing you'll remember about ${objectPronoun}?`
         : clientName  ? `Before you go — one thing you'll remember about ${clientName}?`
         : "Before you go — one thing you'll remember?"}
      </p>
      <DictatedInput ref={inputRef}
        value={text} onChange={(v, meta) => { setText(v); setDictated(!!meta?.dictated); }} onSubmit={save}
        placeholder="Type or tap the mic" ariaLabel="One thing you'll remember"
        disabled={busy} clientId={clientId} />
      {error && <p style={s.error}>{error}</p>}
      {/* Save only once there is something to save; until then the field
          is all there is, and the card's own buttons are the way on. */}
      {text.trim() && (
        <div style={s.row}>
          <button type="button" style={{ ...s.save, ...(busy ? s.disabled : {}) }}
                  onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap:     { display: "flex", flexDirection: "column", gap: "var(--space-3)", width: "100%", maxWidth: 440 },
  q:        { fontFamily: "var(--font-serif)", fontSize: "var(--text-lg)", color: "var(--color-text-primary)", margin: 0 },
  row:      { display: "flex", justifyContent: "flex-end", gap: "var(--space-3)" },
  save:     { font: "inherit", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  disabled: { opacity: 0.5, cursor: "not-allowed" },
  error:    { fontSize: "var(--text-sm)", color: "var(--color-critical)", margin: 0 },
  thanks:   { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0 },
};
